import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminAccountsApi } from '@/services/adminAccounts.service';
import type { AdminAccount, AdminAccountRole } from '@/types/adminAccount.types';
import { inviteAdminFormSchema } from '@/validators/adminAccount.validator';

export const ADMIN_ACCOUNTS_QUERY_KEY = 'admin-accounts';

export interface InviteFormValues {
  name: string;
  email: string;
}

type InviteErrors = Partial<Record<keyof InviteFormValues, string>>;

export interface UseAdminAccountsResult {
  admins: AdminAccount[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  isInviteOpen: boolean;
  inviteForm: InviteFormValues;
  inviteErrors: InviteErrors;
  isInviting: boolean;
  inviteError: string | null;
  inviteSuccess: string | null;
  updatingId: string | null;
  updateError: string | null;
  onOpenInvite: () => void;
  onCloseInvite: () => void;
  onChangeInvite: (field: keyof InviteFormValues, value: string) => void;
  onSubmitInvite: () => void;
  onToggleSuspended: (admin: AdminAccount) => void;
  onResendInvite: (admin: AdminAccount) => void;
  /** Confirmation for the row whose invite was just re-sent; clears on the next action. */
  resentInviteId: string | null;
}

const EMPTY_INVITE: InviteFormValues = { name: '', email: '' };

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

export const useAdminAccounts = (enabled: boolean): UseAdminAccountsResult => {
  const queryClient = useQueryClient();

  const [isInviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteFormValues>(EMPTY_INVITE);
  const [inviteErrors, setInviteErrors] = useState<InviteErrors>({});
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [resentInviteId, setResentInviteId] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: [ADMIN_ACCOUNTS_QUERY_KEY],
    queryFn: () => adminAccountsApi.list(),
    enabled,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [ADMIN_ACCOUNTS_QUERY_KEY] });
  };

  const inviteMutation = useMutation({
    mutationFn: (body: InviteFormValues) => adminAccountsApi.create(body),
    onSuccess: (created) => {
      setInviteSuccess(`Invitation sent to ${created.email}`);
      setInviteForm(EMPTY_INVITE);
      setInviteOpen(false);
      invalidate();
    },
    onError: (error: unknown) => {
      setInviteError(messageOf(error, 'Could not send the invitation.'));
    },
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => adminAccountsApi.resendInvite(id),
    onMutate: (id) => {
      setUpdatingId(id);
      setUpdateError(null);
      setResentInviteId(null);
    },
    onSuccess: (_data, id) => setResentInviteId(id),
    onError: (error: unknown) => {
      setUpdateError(messageOf(error, 'Could not resend that invitation.'));
    },
    onSettled: () => {
      setUpdatingId(null);
      invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) =>
      adminAccountsApi.update(id, { status }),
    onMutate: ({ id }) => {
      setUpdatingId(id);
      setUpdateError(null);
    },
    onError: (error: unknown) => {
      setUpdateError(messageOf(error, 'Could not update that admin.'));
    },
    onSettled: () => {
      setUpdatingId(null);
      invalidate();
    },
  });

  return {
    admins: listQuery.data ?? [],
    isLoading: listQuery.isLoading && listQuery.fetchStatus !== 'idle',
    isError: listQuery.isError,
    errorMessage: listQuery.error instanceof Error ? listQuery.error.message : null,
    isInviteOpen,
    inviteForm,
    inviteErrors,
    isInviting: inviteMutation.isPending,
    inviteError,
    inviteSuccess,
    updatingId,
    updateError,
    onOpenInvite: () => {
      setInviteError(null);
      setInviteSuccess(null);
      setInviteErrors({});
      setInviteOpen(true);
    },
    onCloseInvite: () => setInviteOpen(false),
    onChangeInvite: (field, value) => {
      setInviteErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
      setInviteError(null);
      setInviteForm((prev) => ({ ...prev, [field]: value }));
    },
    onSubmitInvite: () => {
      setInviteError(null);
      setInviteSuccess(null);

      const parsed = inviteAdminFormSchema.safeParse(inviteForm);
      if (!parsed.success) {
        const next: InviteErrors = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path[0] as keyof InviteFormValues;
          if (key && !next[key]) next[key] = issue.message;
        }
        setInviteErrors(next);
        return;
      }
      setInviteErrors({});
      inviteMutation.mutate(parsed.data);
    },
    onToggleSuspended: (admin) =>
      updateMutation.mutate({
        id: admin.id,
        status: admin.status === 'suspended' ? 'active' : 'suspended',
      }),
    onResendInvite: (admin) => resendMutation.mutate(admin.id),
    resentInviteId,
  };
};
